import i18next from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Web3InteractionStep } from '../Web3InteractionStepper/Web3InteractionStepper';
import { useDispatch, useSelector } from 'react-redux';
import { selectUnlockError } from '../../store/claim/claimSlice';
import Button from '@mui/material/Button';
import { PATH_OF_ROUTES } from '../../router/routes';
import { cleanClaim } from '../../store/claim/actions/cleanClaim';
import { useLocation, useNavigate } from 'react-router-dom';
import { claimWeb3InteractionStyles } from './claimWeb3InteractionStyles';
import { OnRamper } from '../OnRamper/OnRamper';
import { useTheme } from '@mui/material/styles';
import ErrorIcon from '@mui/icons-material/Error';
import Icon from '@mui/material/Icon';
import { VestingClaimErrorType } from '../../models/VestingClaimErrorType';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_KEY_ONRAMPER, UNIT_ONRAMPER } from '../../services/endpoints';
import { urlLang } from '../../helpers/langHelper';
import Link from '@mui/material/Link';
import { AnimationLoader } from '../AnimationLoader/AnimationLoader';

export const claimWeb3InteractionSteps: Web3InteractionStep[] = [
  {
    label: () =>
      i18next.t('vesting.home.claim_web3_interaction.steps.claim.label'),
    Description: (): JSX.Element => {
      const { t } = useTranslation();
      const location = useLocation();
      const navigate = useNavigate();
      const dispatch = useDispatch();
      const error = useSelector(selectUnlockError);
      const theme = useTheme();
      const isDark = theme.palette.mode === 'dark';
      const classes = claimWeb3InteractionStyles(theme, isDark);
      const lang = urlLang();
      const onRamperBnbPath = `https://widget.onramper.com/?apiKey=${API_KEY_ONRAMPER}&defaultCrypto=${UNIT_ONRAMPER}&language=${lang}`;
      const continueAction = () => {
        dispatch(cleanClaim());
        navigate(`${PATH_OF_ROUTES.BASE}`, {
          replace: true,
          state: { from: location }
        });
      };
      const linkEmailSupport = `mailto:${t('vesting.support_mail')}`;

      const getErrorMessage = (error: VestingClaimErrorType) => {
        const errorMessageMap = {
          [VestingClaimErrorType.NotEnoughBalanceToPayForFees]: t(
            'vesting.home.claim_web3_interaction.steps.claim.error.not_enough_balance_to_pay_for_fees'
          ),
          [VestingClaimErrorType.CanNotClaimBkn]: t(
            'vesting.home.claim_web3_interaction.steps.claim.error.can_not_claim_bkn.text'
          ),
          [VestingClaimErrorType.UserDeniedTransactionSignature]: t(
            'vesting.home.claim_web3_interaction.steps.claim.error.user_denied.text'
          ),
          [VestingClaimErrorType.PanicContactWithSupport]: (
            <Trans
              i18nKey={
                'vesting.home.claim_web3_interaction.steps.claim.error.panic_contact_support.text'
              }
              components={[
                <Link
                  target='_blank'
                  href={linkEmailSupport}
                  sx={classes.linkSupport}
                />
              ]}
            />
          ),
          [VestingClaimErrorType.GenericError]: t(
            'vesting.home.claim_web3_interaction.steps.claim.error.default_error.text'
          )
        };
        const errorMessage = errorMessageMap[error];
        return errorMessage
          ? errorMessage
          : t(
              'vesting.home.claim_web3_interaction.steps.claim.error.default_error.text'
            );
      };

      return (
        <>
          <Box>
            <Typography
              variant='subtitle2'
              sx={classes.description}
              color='secondary'
            >
              {t(
                'vesting.home.claim_web3_interaction.steps.claim.description.text'
              )}
            </Typography>
            <AnimationLoader />

            {error && (
              <>
                <Box sx={classes.containerIconAndError}>
                  <>
                    <Icon sx={classes.errorIcon}>
                      <ErrorIcon color='error' />
                    </Icon>
                    <Box>
                      <Typography
                        variant='subtitle1'
                        color='error'
                        sx={classes.errorMessage}
                      >
                        {getErrorMessage(error)}
                      </Typography>
                      <OnRamper href={onRamperBnbPath} />
                    </Box>
                  </>
                </Box>
                <Button
                  variant='contained'
                  onClick={continueAction}
                  size='small'
                  sx={classes.errorItem}
                  startIcon={<ArrowBackIcon />}
                >
                  {t(
                    'vesting.home.claim_web3_interaction.steps.claim.button_error.text'
                  )}
                </Button>
              </>
            )}
          </Box>
        </>
      );
    }
  },
  {
    label: () =>
      i18next.t('vesting.home.claim_web3_interaction.steps.success.label'),
    Description: (): JSX.Element => {
      const { t } = useTranslation();
      const theme = useTheme();
      const isDark = theme.palette.mode === 'dark';
      const classes = claimWeb3InteractionStyles(theme, isDark);
      return (
        <Typography
          variant='subtitle2'
          sx={classes.description}
          color='secondary'
        >
          {t(
            'vesting.home.claim_web3_interaction.steps.success.description.text'
          )}
        </Typography>
      );
    }
  }
];
